import React from 'react';
import type { Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import type { UseFieldArrayAppend, UseFieldArrayRemove, FieldArrayWithId } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { UnitToggle } from '../../components/common/UnitToggle';
import type { GbLotOption } from './useSalesOrderForm';
import type { SalesOrderFormData } from '../../types/salesOrder';
import type { InventoryItem } from '../../types/inventory';
import { type WeightUnit } from '../../hooks/useWeightUnit';
import { useLanguage } from '../../features/language/useLanguage';
import { SalesOrderItemRow } from './components/SalesOrderItemRow';
import { SalesOrderTotals } from './components/SalesOrderTotals';

interface Props {
  fields: FieldArrayWithId<SalesOrderFormData, 'items', 'id'>[];
  watchedItems: SalesOrderFormData['items'];
  inventoryItems: InventoryItem[];
  control: Control<SalesOrderFormData>;
  register: UseFormRegister<SalesOrderFormData>;
  setValue: UseFormSetValue<SalesOrderFormData>;
  errors: FieldErrors<SalesOrderFormData>;
  append: UseFieldArrayAppend<SalesOrderFormData, 'items'>;
  remove: UseFieldArrayRemove;
  currencySymbol: string;
  areFieldsDisabled: boolean;
  subtotalAmount: number;
  discountType: 'percent' | 'amount';
  discountValue: number;
  discountAmount: number;
  shippingAmount: number;
  totalAmount: number;
  balanceDue: number;
  quantityLimitErrors: Record<number, string>;
  displayUnit: WeightUnit;
  setDisplayUnit: (unit: WeightUnit) => void;
  ownReservedByItem: Record<string, number>;
  gbLotsByItem: Record<string, GbLotOption[]>;
}

const EMPTY_ITEM = {
  inventoryItemId: '',
  itemType: 'finished_good' as const,
  roastType: '',
  lotId: '',
  lotNumber: '',
  processType: '',
  quantity: 1,
  unitPrice: 0,
  name: '',
  description: '',
};

export const SalesOrderItemsSection: React.FC<Props> = ({
  fields,
  watchedItems,
  inventoryItems,
  control,
  register,
  setValue,
  errors,
  append,
  remove,
  currencySymbol,
  areFieldsDisabled,
  subtotalAmount,
  discountType,
  discountValue,
  discountAmount,
  shippingAmount,
  totalAmount,
  balanceDue,
  quantityLimitErrors,
  displayUnit,
  setDisplayUnit,
  ownReservedByItem,
  gbLotsByItem,
}) => {
  const { t } = useLanguage();
  const hasWeightedItem =
    watchedItems?.some((it) => it?.itemType === 'roasted_bulk' || it?.itemType === 'green_bean') ?? false;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-3">
          <label className="block text-sm font-medium text-gray-700">{t('salesOrder.items.items')}</label>
          <div className="flex gap-2 items-center">
            {hasWeightedItem && <UnitToggle unit={displayUnit} onToggle={setDisplayUnit} disabled={areFieldsDisabled} />}
            {!areFieldsDisabled && (
              <>
                <button
                  data-test-id="so-items-clear-all"
                  type="button"
                  onClick={() => {
                    remove();
                    append({ ...EMPTY_ITEM });
                  }}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t('salesOrder.items.clearAllLines')}
                </button>
                <button
                  data-test-id="so-create-add-item"
                  type="button"
                  onClick={() => append({ ...EMPTY_ITEM })}
                  className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200"
                >
                  <Plus className="h-4 w-4 mr-1" /> {t('salesOrder.items.addLines')}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {fields.map((field, index) => (
            <SalesOrderItemRow
              key={field.id}
              index={index}
              watchedItems={watchedItems}
              inventoryItems={inventoryItems}
              control={control}
              register={register}
              setValue={setValue}
              errors={errors}
              areFieldsDisabled={areFieldsDisabled}
              currencySymbol={currencySymbol}
              displayUnit={displayUnit}
              ownReservedByItem={ownReservedByItem}
              gbLotsByItem={gbLotsByItem}
              quantityLimitErrors={quantityLimitErrors}
              onRemove={remove}
            />
          ))}
          {errors.items && <p className="text-sm text-red-600">{errors.items.message}</p>}
        </div>
      </div>

      <SalesOrderTotals
        subtotalAmount={subtotalAmount}
        discountType={discountType}
        discountValue={discountValue}
        discountAmount={discountAmount}
        shippingAmount={shippingAmount}
        totalAmount={totalAmount}
        balanceDue={balanceDue}
        currencySymbol={currencySymbol}
        areFieldsDisabled={areFieldsDisabled}
        onShippingChange={(value) => setValue('shippingAmount', value, { shouldValidate: true })}
        onDiscountTypeChange={(type) => setValue('discountType', type, { shouldValidate: true })}
        onDiscountValueChange={(value) => setValue('discountValue', value, { shouldValidate: true })}
      />
    </div>
  );
};
